import { IconButton, Box, useToast, Button, Text, Flex, Modal, ModalOverlay, ModalContent, ModalBody, useDisclosure, Tooltip, HStack, useBreakpointValue } from '@chakra-ui/react';
import { FaDropbox } from 'react-icons/fa';
import { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import LoadingSpinner from './LoadingSpinner';
import { formatLoadingMessage } from '../utils/loadingMessageFormatter';
import MobileDownloadWarning from './MobileDownloadWarning';
import { DropboxService } from '../services/DropboxService';
import { getMobileDownloadWarnings } from '../utils/deviceDetection';
import TooltipKeyBox from './TooltipKeyBox';

const DROPBOX_AUTH_FLAG = 'dropboxAuthInProgress';

// Extend Window interface to include Dropbox
declare global {
  interface Window {
    Dropbox?: {
      choose: (options: DropboxChooseOptions) => void;
    };
  }
}

// Define types for Dropbox file objects
interface DropboxFile {
  id: string;
  name: string;
  link: string;
  bytes: number;
  icon: string;
  isDir?: boolean;
  thumbnailLink?: string;
}

// Define types for Dropbox chooser options
interface DropboxChooseOptions {
  success: (files: DropboxFile[]) => void;
  cancel?: () => void;
  linkType?: 'preview' | 'direct';
  multiselect?: boolean;
  filter?: { extensions: string[] };
  folderselect?: boolean;
  sizeLimit?: number;
}

interface DropboxImportProps {
  onFilesSelected: (
    files: File[], 
    originalUrls: string[], 
    shareableUrls: string[]
  ) => void;
}

export interface DropboxImportRef {
  handleDropboxSelect: () => Promise<void>;
}

const DropboxImport = forwardRef<DropboxImportRef, DropboxImportProps>(({ onFilesSelected }, ref) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showContinueDropbox, setShowContinueDropbox] = useState(false);
  const iconSize = useBreakpointValue({ base: 80, md: 80 });
  const [completedCount, setCompletedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [downloadedBytes, setDownloadedBytes] = useState(0);
  const [totalBytes, setTotalBytes] = useState(0);
  const [startedCount, setStartedCount] = useState(0);
  const [serviceError, setServiceError] = useState<Error | null>(null);
  const [pendingFiles, setPendingFiles] = useState<DropboxFile[]>([]);
  const [showMobileWarning, setShowMobileWarning] = useState(false);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { 
    isOpen: isMobileWarningOpen, 
    onOpen: onMobileWarningOpen, 
    onClose: onMobileWarningClose 
  } = useDisclosure();
  
  // Get dropbox service safely
  const getDropboxService = useCallback(() => {
    try {
      return DropboxService.getInstance();
    } catch (error) {
      console.error("[DropboxImport] Failed to get DropboxService instance:", error);
      if (error instanceof Error) {
        setServiceError(error);
      } else {
        setServiceError(new Error("Unknown error initializing Dropbox service"));
      }
      return null;
    }
  }, []);

  // Process files for download
  const processFiles = useCallback(async (files: DropboxFile[], dropboxService: DropboxService) => {
    setCompletedCount(0);
    setTotalCount(files.length);
    setStartedCount(0);
    
    const totalFileBytes = files.reduce((sum, file) => sum + file.bytes, 0);
    setTotalBytes(totalFileBytes);
    setDownloadedBytes(0);

    if (!dropboxService.isAuthenticated()) {
      localStorage.setItem(DROPBOX_AUTH_FLAG, '1');
      await dropboxService.authenticate();
      return;
    }

    const permanentLinks = await dropboxService.getPermanentLinks(
      files.map(file => ({ id: file.id, name: file.name, link: file.link }))
    );

    console.log(`[DropboxImport] Generated ${permanentLinks.length} permanent links`);

    // Track individual file progress
    const fileProgress = new Map<string, { downloaded: number; total: number }>();
    
    const results = await Promise.all(
      permanentLinks.map(async (link, index) => {
        const file = files[index];
        try {
          // Indicate download has started for immediate progress feedback
          console.log(`[DropboxImport] Starting download: ${file.name}`);
          setStartedCount((c) => c + 1);
          
          const blob = await dropboxService.getFileContent(
            file.id, 
            link.url,
            (downloaded, total) => {
              // Update individual file progress
              fileProgress.set(file.id, { downloaded, total });
              
              // Calculate cumulative downloaded bytes across ALL files
              let totalDownloaded = 0;
              
              // Sum up downloaded bytes from all files that have started
              files.forEach(f => {
                const progress = fileProgress.get(f.id);
                if (progress && progress.downloaded > 0) {
                  // File is being downloaded, add its downloaded bytes
                  totalDownloaded += progress.downloaded;
                }
              });
              
              // Update total downloaded bytes (cumulative across all files)
              setDownloadedBytes(totalDownloaded);
              
              // Don't update totalBytes here - it's already set upfront with the sum of all file.bytes
              // Only update if we discover a file is larger than estimated (but this is rare for Dropbox)
            }
          );
          
          console.log(`[DropboxImport] Successfully processed: ${file.name}`);
          console.log(`[DropboxImport] Blob type: "${blob.type}", size: ${blob.size} bytes`);
          
          // Ensure we have a proper MIME type for audio files
          let mimeType = blob.type;
          if (!mimeType || mimeType === 'application/octet-stream') {
            // Try to determine MIME type from file extension
            const extension = file.name.toLowerCase().split('.').pop();
            switch (extension) {
              case 'mp3':
                mimeType = 'audio/mpeg';
                break;
              case 'wav':
                mimeType = 'audio/wav';
                break;
              case 'm4a':
                mimeType = 'audio/mp4';
                break;
              case 'aac':
                mimeType = 'audio/aac';
                break;
              case 'ogg':
                mimeType = 'audio/ogg';
                break;
              case 'flac':
                mimeType = 'audio/flac';
                break;
              default:
                mimeType = 'audio/mpeg'; // fallback
            }
            console.log(`[DropboxImport] Inferred MIME type: "${mimeType}" for extension: ${extension}`);
          }
          
          setCompletedCount((c) => c + 1);
          
          return {
            file: new File([blob], file.name, { type: mimeType }),
            originalUrl: file.link,
            shareableUrl: link.url
          };
        } catch (fileError) {
          console.error(`[DropboxImport] Failed to process file: ${file.name}`, fileError);
          throw fileError;
        }
      })
    );

    onFilesSelected(
      results.map(r => r.file),
      results.map(r => r.originalUrl),
      results.map(r => r.shareableUrl)
    );
  }, [onFilesSelected]);

  // Handle mobile warning continue
  const handleMobileWarningContinue = useCallback(async () => {
    const dropboxService = getDropboxService();
    if (!dropboxService || pendingFiles.length === 0) return;

    try {
      // Set loading state BEFORE processing files so spinner shows immediately
      setIsLoading(true);
      await processFiles(pendingFiles, dropboxService);
    } catch (error: unknown) {
      console.error('[DropboxImport] Error processing files:', error);
      toast({
        title: 'Error Importing Files',
        description: error instanceof Error ? error.message : 'Failed to import files from Dropbox',
        status: 'error',
        duration: 9000,
        isClosable: true
      });
    } finally {
      setIsLoading(false);
      setCompletedCount(0);
      setTotalCount(0);
      setStartedCount(0);
      setDownloadedBytes(0);
      setTotalBytes(0);
      setPendingFiles([]);
    }
  }, [getDropboxService, pendingFiles, processFiles, toast]);
  
  // Check for OAuth tokens in URL on component mount
  useEffect(() => {
    // Parse hash fragment for Dropbox OAuth token
    const parseHashFragment = () => {
      const hash = window.location.hash;
      if (hash && hash.includes('access_token=')) {
        try {
          // Basic parsing of hash fragment
          const accessToken = hash
            .substring(1)
            .split('&')
            .find(pair => pair.startsWith('access_token='))
            ?.split('=')[1];
            
          if (accessToken) {
            // Save the token to localStorage
            localStorage.setItem('dropboxAccessToken', accessToken);
            console.log('[DropboxImport] Successfully stored Dropbox access token');
            
            // Clear the hash fragment from URL
            window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
            
            // Check for any pending Dropbox operations
            const isPending = localStorage.getItem(DROPBOX_AUTH_FLAG) === '1';
            if (isPending) {
              setShowContinueDropbox(true);
              onOpen(); // Open the modal when showing continue button
            }
            
            return true;
          }
        } catch (error) {
          console.error('[DropboxImport] Error parsing hash fragment:', error);
        }
      }
      return false;
    };
    
    // Run the parser
    parseHashFragment();
  }, [onOpen]);

  const openDropboxPicker = useCallback(async () => {
    const dropboxService = getDropboxService();
    if (!dropboxService) {
      toast({
        title: "Dropbox Service Error",
        description: "Failed to initialize Dropbox service",
        status: "error",
        duration: 5000,
        isClosable: true
      });
      return;
    }
    
    await loadDropboxSDK();
    if (!window.Dropbox) {
      toast({
        title: "Dropbox SDK Error",
        description: "Failed to load Dropbox SDK",
        status: "error",
        duration: 5000,
        isClosable: true
      });
      return;
    }

    window.Dropbox.choose({
      success: async (files: DropboxFile[]) => {
        let showingMobileWarning = false;
        
        try {
          console.log(`[DropboxImport] Processing ${files.length} files`);
          
          // Calculate total bytes for progress tracking
          const totalFileBytes = files.reduce((sum, file) => sum + file.bytes, 0);
          console.log(`[DropboxImport] Total download size: ${(totalFileBytes / 1024 / 1024).toFixed(2)} MB`);
          
          // Check for mobile warnings
          const warnings = getMobileDownloadWarnings(totalFileBytes);
          console.log('[DropboxImport] Mobile warnings:', warnings);
          
          if (warnings.shouldWarnDataUsage || warnings.shouldWarnLargeSize) {
            // Store files and show warning modal
            setPendingFiles(files);
            setTotalBytes(totalFileBytes);
            onMobileWarningOpen();
            showingMobileWarning = true;
            return;
          }
          
          // Set loading state BEFORE processing files so spinner shows immediately
          setIsLoading(true);
          
          // Proceed with download directly if no warnings
          await processFiles(files, dropboxService);
        } catch (error: unknown) {
          console.error('[DropboxImport] Error in file selection:', error);
          toast({
            title: 'Error',
            description: error instanceof Error ? error.message : 'Failed to process file selection',
            status: 'error',
            duration: 5000,
            isClosable: true
          });
        } finally {
          // Only reset state if we're not showing mobile warning
          if (!showingMobileWarning) {
            setIsLoading(false);
            setCompletedCount(0);
            setTotalCount(0);
            setStartedCount(0);
            setDownloadedBytes(0);
            setTotalBytes(0);
          }
        }
      },
      cancel: () => setIsLoading(false),
      linkType: "preview", // Using "preview" gives us a permanent shared link we can convert reliably
      multiselect: true,
      filter: { extensions: ['mp3', 'wav', 'm4a', 'aac', 'ogg', 'flac'] }
    });
  }, [getDropboxService, onFilesSelected, toast]);

  const handleDropboxSelect = useCallback(async () => {
    const dropboxService = getDropboxService();
    if (!dropboxService) {
      toast({
        title: "Dropbox Service Error",
        description: "Failed to initialize Dropbox service",
        status: "error",
        duration: 5000,
        isClosable: true
      });
      return;
    }
    
    if (!dropboxService.isAuthenticated()) {
      localStorage.setItem(DROPBOX_AUTH_FLAG, '1');
      await dropboxService.authenticate();
      return;
    }
    
    // If SDK is already loaded, call choose() synchronously to preserve user gesture
    if (window.Dropbox) {
      try {
        window.Dropbox.choose({
          success: async (files: DropboxFile[]) => {
            let showingMobileWarning = false;
            
            try {
              console.log(`[DropboxImport] Processing ${files.length} files`);
              
              const totalFileBytes = files.reduce((sum, file) => sum + file.bytes, 0);
              console.log(`[DropboxImport] Total download size: ${(totalFileBytes / 1024 / 1024).toFixed(2)} MB`);
              
              const warnings = getMobileDownloadWarnings(totalFileBytes);
              console.log('[DropboxImport] Mobile warnings:', warnings);
              
              if (warnings.shouldWarnDataUsage || warnings.shouldWarnLargeSize) {
                setPendingFiles(files);
                setTotalBytes(totalFileBytes);
                onMobileWarningOpen();
                showingMobileWarning = true;
                return;
              }
              
              // Set loading state BEFORE processing files so spinner shows immediately
              setIsLoading(true);
              
              await processFiles(files, dropboxService);
            } catch (error: unknown) {
              console.error('[DropboxImport] Error in file selection:', error);
              toast({
                title: 'Error',
                description: error instanceof Error ? error.message : 'Failed to process file selection',
                status: 'error',
                duration: 5000,
                isClosable: true
              });
            } finally {
              if (!showingMobileWarning) {
                setIsLoading(false);
                setCompletedCount(0);
                setTotalCount(0);
                setStartedCount(0);
                setDownloadedBytes(0);
                setTotalBytes(0);
              }
            }
          },
          cancel: () => setIsLoading(false),
          linkType: "preview",
          multiselect: true,
          filter: { extensions: ['mp3', 'wav', 'm4a', 'aac', 'ogg', 'flac'] }
        });
        return;
      } catch (error) {
        console.error('[DropboxImport] Error calling window.Dropbox.choose() synchronously:', error);
        // Fall through to async path
      }
    }
    
    // Fallback to async path if SDK not loaded
    await openDropboxPicker();
  }, [getDropboxService, toast, processFiles, onMobileWarningOpen, openDropboxPicker]);

  // Expose handleDropboxSelect method via ref
  useImperativeHandle(ref, () => ({
    handleDropboxSelect
  }), [handleDropboxSelect]);

  // Check for continuation flag on mount
  useEffect(() => {
    const dropboxService = getDropboxService();
    if (dropboxService && dropboxService.isAuthenticated() && localStorage.getItem(DROPBOX_AUTH_FLAG)) {
      localStorage.removeItem(DROPBOX_AUTH_FLAG);
      setShowContinueDropbox(true);
      onOpen(); // Open the modal when showing continue button
    }
  }, [getDropboxService, onOpen]);

  const handleContinueDropbox = async () => {
    setShowContinueDropbox(false);
    onClose(); // Close the modal
    await openDropboxPicker();
  };

  const loadDropboxSDK = (): Promise<void> => {
    return new Promise((resolve) => {
      if (window.Dropbox) return resolve();
      
      const script = document.createElement('script');
      script.src = 'https://www.dropbox.com/static/api/2/dropins.js';
      script.id = 'dropboxjs';
      script.setAttribute('data-app-key', '7avzllilqvi9a9p');
      script.onload = () => resolve();
      script.onerror = () => {
        console.error('[DropboxImport] Failed to load Dropbox SDK');
        resolve(); // Resolve anyway to avoid hanging
      };
      document.body.appendChild(script);
    });
  };

  // If there's a service error, render a fallback
  if (serviceError) {
    return (
      <Box position="relative">
        <IconButton
          aria-label="Import from Dropbox (unavailable)"
          data-testid="dropbox-import-button"
          icon={<FaDropbox size={iconSize} opacity={0.5} />}
          colorScheme="gray"
          variant="ghost"
          onClick={() => {
            toast({
              title: "Dropbox Unavailable",
              description: "Dropbox import is currently unavailable",
              status: "warning",
              duration: 5000,
              isClosable: true
            });
          }}
          size="lg"
          width={{ base: '150px', md: '150px' }}
          height={{ base: '150px', md: '150px' }}
          borderRadius="2xl"
        />
      </Box>
    );
  }

  return (
    <Box position="relative">
      <Tooltip 
        label={
          <HStack spacing={2} color="gray.800">
            <Text>Import from Dropbox</Text>
            <TooltipKeyBox>D</TooltipKeyBox>
          </HStack>
        } 
        placement="top"
        offset={[0, -35]}
      >
        <IconButton
          aria-label="Import from Dropbox"
          data-testid="dropbox-import-button"
          icon={<FaDropbox size={iconSize} />}
          colorScheme="blue"
          variant="ghost"
          onClick={handleDropboxSelect}
          size="lg"
          width={{ base: '150px', md: '150px' }}
          height={{ base: '150px', md: '150px' }}
          borderRadius="2xl"
          isDisabled={showContinueDropbox}
          _hover={{
            transform: 'scale(1.05)',
            borderColor: 'transparent'
          }}
          _active={{
            transform: 'scale(0.95)',
            borderColor: 'transparent'
          }}
        />
      </Tooltip>
      
      {/* Modal for continue Dropbox import button */}
      <Modal isOpen={isOpen && showContinueDropbox} onClose={onClose} isCentered>
        <ModalOverlay backdropFilter="blur(2px)" bg="blackAlpha.600" />
        <ModalContent bg="rgba(20, 20, 30, 0.9)" borderRadius="xl" p={4} width="auto">
          <ModalBody>
            <Flex direction="column" align="center" justify="center">
              <Button 
                colorScheme="blue" 
                onClick={handleContinueDropbox}
                size="md"
                px={6}
                py={5}
              >
                Click to continue Dropbox import
              </Button>
              <Text mt={2} fontSize="xs" color="gray.400">
                to enable setup sharing
              </Text>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
      
      {isLoading && (
        <LoadingSpinner 
          message={formatLoadingMessage('Importing files from Dropbox...', {
            totalBytes,
            downloadedBytes,
          })} 
        />
      )}

      {/* Mobile Download Warning Modal */}
      <MobileDownloadWarning
        isOpen={isMobileWarningOpen}
        onClose={() => {
          onMobileWarningClose();
          setPendingFiles([]);
          setTotalBytes(0);
        }}
        onContinue={handleMobileWarningContinue}
        sizeMB={Math.round(totalBytes / (1024 * 1024))}
        shouldWarnDataUsage={pendingFiles.length > 0 && getMobileDownloadWarnings(totalBytes).shouldWarnDataUsage}
        shouldWarnLargeSize={pendingFiles.length > 0 && getMobileDownloadWarnings(totalBytes).shouldWarnLargeSize}
        networkType={pendingFiles.length > 0 ? getMobileDownloadWarnings(totalBytes).networkInfo.effectiveType : 'unknown'}
      />
    </Box>
  );
});

DropboxImport.displayName = 'DropboxImport';

export default DropboxImport;